import { ShieldAlert, ExternalLink } from "lucide-react";
import ActionPlan from "./ActionPlan";

const money = (n) => (typeof n === "number" ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "—");

const LEVEL = {
  Low: { bar: "bg-[#4b7a3a]", text: "text-[#4b7a3a]" },
  Moderate: { bar: "bg-[#a3823f]", text: "text-[#a3823f]" },
  Elevated: { bar: "bg-[#8f4f2b]", text: "text-[#8f4f2b]" },
  High: { bar: "bg-[#b23a48]", text: "text-[#b23a48]" },
};

export default function FraudRiskCard({ risk, plan, finRef, businessName }) {
  if (!risk || (risk.score == null && !(risk.flags || []).length)) return null;
  const score = Math.max(0, Math.min(100, Number(risk.score) || 0));
  const level = risk.level || (score >= 70 ? "High" : score >= 45 ? "Elevated" : score >= 25 ? "Moderate" : "Low");
  const tone = LEVEL[level] || LEVEL.Moderate;
  const flags = risk.flags || [];
  const hasExposure = risk.exposure_low != null || risk.exposure_high != null;

  return (
    <div data-testid="fraud-risk-card" className="rounded-2xl border border-[#8f4f2b]/30 bg-white p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#8f4f2b] flex items-center gap-1.5">
            <ShieldAlert className="h-3.5 w-3.5" /> Fraud &amp; irregularity risk · public-signal scan
          </p>
          {risk.summary && <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#1e3d3a]/80">{risk.summary}</p>}
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">Est. exposure / yr</div>
          <div data-testid="fraud-exposure" className="mt-1 font-head text-2xl text-[#1e3d3a]">
            {hasExposure ? `${money(risk.exposure_low || 0)} – ${money(risk.exposure_high ?? risk.exposure_low ?? 0)}` : "—"}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="h-2.5 w-full rounded-full bg-[#eae7de] overflow-hidden">
          <div className={`h-full rounded-full ${tone.bar} transition-[width] duration-700`} style={{ width: `${score}%` }} />
        </div>
        <p data-testid="fraud-score" className={`mt-1.5 text-xs font-semibold ${tone.text}`}>{score}/100 · {level} risk</p>
      </div>

      {flags.length > 0 ? (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/45">
                <th className="py-2 pr-3 font-normal">Pattern</th>
                <th className="py-2 pr-3 font-normal">Evidence</th>
                <th className="py-2 pr-3 font-normal">Source</th>
                <th className="py-2 text-right font-normal">Est. $ / yr</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((fl, i) => (
                <tr key={i} data-testid={`fraud-flag-${i}`} className="border-t border-[#1e3d3a]/8 align-top">
                  <td className="py-2 pr-3 font-medium text-[#1e3d3a]">{fl.pattern}</td>
                  <td className="py-2 pr-3 text-[#1e3d3a]/75 leading-relaxed">{fl.evidence}</td>
                  <td className="py-2 pr-3 text-[#1e3d3a]/60 whitespace-nowrap">
                    {fl.source_url ? (
                      <a href={fl.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#a3823f] hover:text-[#1e3d3a]">{fl.source} <ExternalLink className="h-3 w-3" /></a>
                    ) : fl.source}
                  </td>
                  <td className="py-2 text-right tabular-nums text-[#1e3d3a] whitespace-nowrap">
                    {fl.est_exposure_low != null || fl.est_exposure_high != null ? `${money(fl.est_exposure_low || 0)}–${money(fl.est_exposure_high ?? fl.est_exposure_low ?? 0)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-4 text-xs text-[#1e3d3a]/55">No specific irregularity patterns were flagged in the public record.</p>
      )}
      <ActionPlan plan={plan} testid="fraud-plan" chartKey="fraud" finRef={finRef} businessName={businessName} />
      <p className="mt-3 text-[10px] leading-relaxed text-[#1e3d3a]/45 italic">
        {risk.method || "Score and exposure are estimates from public BBB complaint patterns, review text and SEC filings. They indicate where to investigate — they are not a finding of fraud."}
      </p>
    </div>
  );
}
